import * as React from 'react';
import { Button, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { Languages } from 'src/localization/Locale';
import { AppSession } from 'src/models/AppSession';
import { ILoginLibrary, InviteType, IPermission } from 'src/models/dto/DashboardModels';
import { AppContext } from 'src/ui/state/Contextes';
import { Convert } from 'src/utilities/Helpers';

import * as Messages from '../../foundation/Messages';

export interface IPermissionInviteFormProps {
  permission: IPermission;
  wrapInForm?: boolean;
  reloadPermissions: () => void;
  goBackBtnClicked?: () => void;
}
export interface IPermissionInviteFormState {
  currentInviteType: InviteType;
  userId: number | null;
  userName: string | null;
  email: string | null;
  publisherId: number | null;
  completedLink: string | null;
}
export class PermissionInviteForm extends React.Component<IPermissionInviteFormProps, IPermissionInviteFormState> {
  context: AppSession;
  static contextType = AppContext;
  linkRef = React.createRef<HTMLInputElement>();
  constructor(props: IPermissionInviteFormProps) {
    super(props);
    this.state = { currentInviteType: InviteType.DirectCreateUsername, email: null, userId: null, userName: null, publisherId: null, completedLink: null };
  }
  componentDidMount() {
    let pub = this.context.loginLibraries.get("prolibro");
    if (!pub) {
      pub = this.context.loginLibraries.rows()[0];
    }
    this.setState({ publisherId: pub.PublisherId });
  }
  componentWillUnmount() {
    this.props.reloadPermissions();
  }
  describePermission = (): JSX.Element => {
    let perms: JSX.Element[] = [];
    if (this.props.permission.ManageSystem) {
      perms.push(
        <li key="system">
          Can manage the <b>entire system.</b>
        </li>
      );
    }
    if (this.props.permission.ManageAccounts) {
      perms.push(
        <li key="accounts">
          Can manage the accounts within <b>entire system.</b> And assign new non-administrative permissions.
        </li>
      );
    }
    if (this.props.permission.ManagePublishers) {
      perms.push(
        <li key="publisher">
          Can manage the properties of the publisher and interact with all child elements such as <b>Subscription, Products and Titles</b>. Highest level of
          power that is not admin. Can modify and view the <b>LibraryConfig</b>.
        </li>
      );
    }
    if (this.props.permission.ManageProducts) {
      perms.push(
        <li key="product">
          Can manage the products for the publisher. This does not give access to <b>Subscriptions</b> or <b>Titles</b>.
        </li>
      );
    }
    if (this.props.permission.ManageTitles) {
      perms.push(
        <li key="title">
          Can manage the titles for the publisher. Will see all titles in the <b>Reader</b> and can use Build to publish and upload new content.
        </li>
      );
    }
    if (this.props.permission.ManageSubscription && this.props.permission.SubscriptionId === null) {
      perms.push(
        <li key="subscription">
          Can manage all the <b>subscriptions</b> for the publisher. This includes their <b>creation, attribution, removal and customer management</b>.
        </li>
      );
    }
    if (this.props.permission.ManageSubscription && this.props.permission.SubscriptionId !== null) {
      perms.push(
        <li key="subscription">
          Can attribute Licences to a subscription with ID <b>{this.props.permission.SubscriptionId}</b>.
        </li>
      );
    }
    return (
      <div>
        <span>
          You are creating a permission expiring{" "}
          <b>
            {this.props.permission.ExpirationDate === null ? "Never" : Convert.dateToFormattedString(this.props.permission.ExpirationDate, Languages.English)}
          </b>{" "}
          granting the following abilities on publisher{" "}
          <b>
            {this.props.permission.PublisherId !== null
              ? this.context.loginLibraries.rows().find((x) => this.props.permission.PublisherId === x.PublisherId)!.DisplayName
              : "All"}
          </b>
          :
        </span>
        <ul>{perms}</ul>
      </div>
    );
  };
  inviteTypeChanged = (arg: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ currentInviteType: +arg.target.value });
  };
  userIdChanged = (arg: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ userId: +arg.target.value });
  };
  usernameChanged = (arg: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ userName: arg.target.value });
  };
  directCreateUserIdPermission = async () => {
    let holdPerm = this.props.permission;
    holdPerm.UserId = this.state.userId!;
    let response = await this.context.insertOrUpdatePermission({ Permission: holdPerm });
    if (response.valid()) {
      Messages.Notify.success(`The Permission item was saved successfully!`);
      this.props.reloadPermissions();
      if (this.props.goBackBtnClicked) {
        this.props.goBackBtnClicked();
      }
    } else {
      if (response.errors.length > 0) {
        Messages.Notify.error("Save failed. Server reported: " + response.errors[0].Message);
      } else {
        Messages.Notify.error("An error occurred while executing the communication");
      }
    }
  };
  directCreateUsername = async () => {
    let response = await this.context.createPermissionByUsername({
      Permission: this.props.permission,
      Username: this.state.userName!,
      PublisherId: this.state.publisherId!,
    });
    if (response.valid()) {
      Messages.Notify.success(`The Permission item was saved successfully!`);
      this.props.reloadPermissions();
      if (this.props.goBackBtnClicked) {
        this.props.goBackBtnClicked();
      }
    } else {
      if (response.errors.length > 0) {
        Messages.Notify.error("Save failed. Server reported: " + response.errors[0].Message);
      } else {
        Messages.Notify.error("An error occurred while executing the communication");
      }
    }
  };

  inviteEmail = async () => {
    if (this.state.email === null || Convert.isEmptyOrSpaces(this.state.email)) {
      Messages.Notify.error("Please enter an email in the field to send the invite.");
      return;
    }
    let response = await this.context.createPermissionInvitationWithEmail({ Permission: this.props.permission, Email: this.state.email });
    if (response.valid()) {
      Messages.Notify.success(`The Permission item was saved successfully and the email was sent!`);
      if (this.props.goBackBtnClicked) {
        this.props.goBackBtnClicked();
      }
    } else {
      if (response.errors.length > 0) {
        Messages.Notify.error("Save failed. Server reported: " + response.errors[0].Message);
      } else {
        Messages.Notify.error("An error occurred while executing the communication");
      }
    }
  };
  inviteLink = async () => {
    let response = await this.context.createPermissionInvitationWithLink({ Permission: this.props.permission });
    if (response.valid()) {
      Messages.Notify.success(`The Permission item was saved successfully and copied to clipboard!`);
      this.setState({ completedLink: response.data.Link }, () => {
        this.setLinkCopy(false);
      });
    } else {
      if (response.errors.length > 0) {
        Messages.Notify.error("Save failed. Server reported: " + response.errors[0].Message);
      } else {
        Messages.Notify.error("An error occurred while executing the communication");
      }
    }
  };
  setLinkCopy = (showMessage: boolean) => {
    this.linkRef.current!.select();
    document.execCommand("copy");
    if (showMessage) {
      Messages.Notify.success(`Copied to clipboard!`);
    }
  };
  publisherChanged = (arg: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ publisherId: +arg.target.value });
  };
  emailChanged = (arg: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ email: arg.target.value });
  };
  formValid = () => {
    switch (this.state.currentInviteType) {
      case InviteType.DirectCreateUserId:
        return this.state.userId !== null && this.state.userId > 0;
      case InviteType.DirectCreateUsername:
        return this.state.userName !== null && !Convert.isEmptyOrSpaces(this.state.userName);
      case InviteType.InviteEmail:
        return this.state.email !== null && !Convert.isEmptyOrSpaces(this.state.email);
      case InviteType.InviteLink:
        return true;
      case InviteType.InviteCsv:
        return true;
    }
  };
  render() {
    let button: JSX.Element;

    switch (this.state.currentInviteType) {
      case InviteType.DirectCreateUserId:
        button = (
          <Button disabled={!this.formValid()} onClick={this.directCreateUserIdPermission} outline color="primary">
            Create Permission
          </Button>
        );
        break;
      case InviteType.DirectCreateUsername:
        button = (
          <Button disabled={!this.formValid()} onClick={this.directCreateUsername} outline color="primary">
            Create Permission
          </Button>
        );
        break;
      case InviteType.InviteEmail:
        button = (
          <Button disabled={!this.formValid()} onClick={this.inviteEmail} outline color="primary">
            Send invite
          </Button>
        );
        break;
      case InviteType.InviteLink:
        button = (
          <Button disabled={!this.formValid()} onClick={this.inviteLink} outline color="primary">
            Get Link
          </Button>
        );
        break;
      case InviteType.InviteCsv:
        button = (
          <Button disabled={!this.formValid()} onClick={() => {}} outline color="primary">
            Send Invites
          </Button>
        );
    }
    if (this.state.completedLink !== null) {
      button = <Input onClick={() => this.setLinkCopy(true)} innerRef={this.linkRef} type="text" readOnly value={this.state.completedLink} />;
    }

    let wrapper: JSX.Element;
    let pub: ILoginLibrary | null = null;
    if (this.state.publisherId === null) {
      pub = this.context.loginLibraries.get("prolibro")!;
    }
    if (this.state.publisherId === null && !pub) {
      pub = this.context.loginLibraries.rows()[0]!;
    }
    if (this.state.publisherId !== null) {
      pub = this.context.loginLibraries.rows().find((x) => x.PublisherId === this.state.publisherId)!;
    }
    let contents = (
      <Col>
        <Row>
          {this.describePermission()}
          <p>Permissions in the Dashboard can be granted in 4 ways. You may choose one of the following options:</p>
          <ol>
            <li>If you know the (case-sensitive) login name of the user and their login Publisher, you can attribute the permission directly.</li>
            <li>If you know the User ID, you can attribute the permission directly.</li>
            <li>
              If you want to send the invitation yourself, a link can be generated that you can send to the person so they can go through the acceptance steps.
            </li>
            <li>If you wish for Dashboard to contact the user by email, input the email and Dashboard will send an email invitation directly.</li>
          </ol>
          <p>For options 3 and 4, the permission will only become active once the user accepts it and links it to a Dashboard account.</p>
        </Row>
        <Row>
          <FormGroup style={{ marginLeft: "15px", marginRight: "15px", flex: "1" }}>
            <Input type="select" name="permissionInviteType" value={this.state.currentInviteType} onChange={this.inviteTypeChanged}>
              <option value={InviteType.DirectCreateUsername}>Invite by username</option>
              <option value={InviteType.DirectCreateUserId}>Invite by user ID</option>
              <option value={InviteType.InviteEmail}>Invite by email</option>
              <option value={InviteType.InviteLink}>Invite by link</option>
            </Input>
          </FormGroup>
        </Row>
        {this.state.currentInviteType === InviteType.DirectCreateUserId && (
          <Row>
            <FormGroup style={{ marginLeft: "15px", marginRight: "15px", flex: "1" }}>
              <Label for="userId">User ID</Label>
              <Input type="number" name="userId" defaultValue={this.state.userId === null ? 0 : this.state.userId} onChange={this.userIdChanged} />
            </FormGroup>
          </Row>
        )}
        {this.state.currentInviteType === InviteType.DirectCreateUsername && (
          <Row>
            <FormGroup style={{ marginLeft: "15px", marginRight: "15px", flex: "1" }}>
              <Label for="userId">Username</Label>
              <Input
                type="text"
                name="username"
                defaultValue={this.state.userName === null ? undefined : this.state.userName}
                onChange={this.usernameChanged}
              />
            </FormGroup>
            <FormGroup style={{ marginLeft: "15px", marginRight: "15px", flex: "1" }}>
              <Label>Publisher the user logs in with</Label>
              <Input type="select" name="brandKey" value={pub!.PublisherId} onChange={this.publisherChanged}>
                {this.context.loginLibraries
                  .rows()
                  .sort((a, b) => (a.DisplayName > b.DisplayName ? 1 : -1))
                  .map((lib) => (
                    <option value={lib.PublisherId} key={lib.PublisherId}>
                      {lib.DisplayName}
                    </option>
                  ))}
              </Input>
            </FormGroup>
          </Row>
        )}
        {this.state.currentInviteType === InviteType.InviteEmail && (
          <Row>
            <FormGroup style={{ marginLeft: "15px", marginRight: "15px", flex: "1" }}>
              <Label for="userId">Email</Label>
              <Input type="email" name="username" defaultValue={this.state.email === null ? undefined : this.state.email} onChange={this.emailChanged} />
            </FormGroup>
          </Row>
        )}
        <Row className="formButtons">
          {button}
          {this.props.goBackBtnClicked && (
            <Button onClick={this.props.goBackBtnClicked} style={{ marginLeft: "10px" }} outline color="info">
              Go back
            </Button>
          )}
        </Row>
      </Col>
    );
    if (this.props.wrapInForm) {
      wrapper = <Form>{contents}</Form>;
    } else {
      wrapper = contents;
    }
    return wrapper;
  }
}
